// Scene — the shared "picture + words" building blocks.
//
// Every panel, dialog and toast that wants art composes these instead of
// hand-rolling its own box: `SceneImage` is the picture (aspect, fade-in,
// scrim, graceful fallback when a file is missing) and `Callout` is the toast
// / inline card surface (media + title + body + actions). Dialogs stay the
// radix `Dialog`; a `SceneImage` goes at the top of its content.
//
// Colours come from the theme tokens, so the surface follows light and dark;
// a surface that must stay dark pins the `.dark` token set on a parent.

import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils.js';

const sceneImageVariants = cva('relative block w-full overflow-hidden bg-muted', {
  variants: {
    /** `card` 4:5 · `wide` 3:2 · `cinema` 16:9 · `square` 1:1 · `strip` 3:1 */
    aspect: {
      card: 'aspect-[4/5]',
      wide: 'aspect-[3/2]',
      cinema: 'aspect-video',
      square: 'aspect-square',
      strip: 'aspect-[3/1]',
    },
    rounded: {
      none: 'rounded-none',
      md: 'rounded-md',
      xl: 'rounded-xl',
    },
  },
  defaultVariants: { aspect: 'wide', rounded: 'md' },
});

export interface SceneImageProps
  extends Omit<React.ComponentProps<'figure'>, 'children'>,
    VariantProps<typeof sceneImageVariants> {
  readonly src: string;
  /** A smaller file for narrow slots; the browser picks by `sizes`. */
  readonly srcSmall?: string;
  /** Intrinsic width of `src` / `srcSmall`, for the `srcset` descriptors. */
  readonly widths?: readonly [small: number, large: number];
  readonly sizes?: string;
  /** Describes the scene; leave empty when the surrounding text already does. */
  readonly alt?: string;
  /** A line over the bottom of the picture. */
  readonly caption?: React.ReactNode;
  /** Which part of the picture survives the cover crop: `top` keeps heads and faces. */
  readonly focus?: 'center' | 'top';
  /** Darken the lower edge so text and controls stay legible over any art. */
  readonly scrim?: boolean;
}

function SceneImage({
  src,
  srcSmall,
  widths,
  sizes,
  alt = '',
  caption,
  scrim = true,
  focus = 'center',
  aspect,
  rounded,
  className,
  ...props
}: SceneImageProps) {
  const [failed, setFailed] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const srcSet = srcSmall && widths ? `${srcSmall} ${widths[0]}w, ${src} ${widths[1]}w` : undefined;
  return (
    <figure
      data-slot="scene-image"
      data-state={failed ? 'missing' : loaded ? 'loaded' : 'loading'}
      className={cn(sceneImageVariants({ aspect, rounded }), 'm-0', className)}
      {...props}
    >
      {!failed && (
        <img
          src={src}
          srcSet={srcSet}
          sizes={sizes}
          alt={alt}
          loading="lazy"
          decoding="async"
          draggable={false}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={cn(
            'absolute inset-0 size-full object-cover transition-opacity duration-300',
            focus === 'top' ? 'object-top' : 'object-center',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
      {scrim && !failed && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-linear-to-t from-black/70 to-transparent"
        />
      )}
      {caption && (
        <figcaption className="absolute inset-x-0 bottom-0 p-2 text-xs font-medium text-white drop-shadow">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

const calloutVariants = cva(
  'pointer-events-auto flex w-full flex-wrap items-center gap-3 rounded-xl border bg-card/95 px-4 py-2.5 text-left text-card-foreground shadow-lg backdrop-blur-sm',
  {
    variants: {
      tone: {
        neutral: 'border-border',
        gold: 'border-amber-400/50 shadow-amber-400/15',
        danger: 'border-destructive/50 shadow-destructive/15',
        info: 'border-sky-400/50 shadow-sky-400/15',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface CalloutProps
  extends Omit<React.ComponentProps<'div'>, 'title'>,
    VariantProps<typeof calloutVariants> {
  /** A picture or portrait on the left. */
  readonly media?: React.ReactNode;
  readonly title: React.ReactNode;
  readonly description?: React.ReactNode;
  /** Buttons on the right (wraps beneath on a narrow screen). */
  readonly actions?: React.ReactNode;
}

function Callout({
  media,
  title,
  description,
  actions,
  tone,
  className,
  children,
  ...props
}: CalloutProps) {
  return (
    <div data-slot="callout" className={cn(calloutVariants({ tone }), className)} {...props}>
      {media && (
        <span data-slot="callout-media" className="flex shrink-0 gap-1.5">
          {media}
        </span>
      )}
      <span className="flex min-w-[10rem] flex-1 flex-col">
        <span data-slot="callout-title" className="font-serif text-sm leading-tight">
          {title}
        </span>
        {description && (
          <span
            data-slot="callout-description"
            className="text-xs leading-snug text-muted-foreground"
          >
            {description}
          </span>
        )}
        {children}
      </span>
      {actions && (
        <span data-slot="callout-actions" className="flex shrink-0 gap-2">
          {actions}
        </span>
      )}
    </div>
  );
}

export { SceneImage, Callout, sceneImageVariants, calloutVariants };
